// Running a saved macro that has variables from the macros bar (the editor's and the object page's): a dialog first,
// a row for each variable its steps use with its default filled in; Run runs it with the values there (for this run:
// the saved defaults stay as they are), Cancel / Esc doesn't run it. A macro without variables runs straight away.
import { type Env, type Field, type Macro, fieldValue, takes, varsOf } from './macros.ts';

/** An input for a value f takes: get reads it, put writes it when f takes it (say: why it didn't). */
export type FieldInput = (f: Field, get: () => unknown, put: (v: number | string | boolean) => void, say: (msg: string, warn?: boolean) => void) => HTMLElement;

/** The field for a variable no step's input says the kind of (one only passed on to a macro not saved): any text. */
export const anyField = (key: string): Field => ({ k: 'src', key, label: '', dflt: '' });

const el = (tag: string, attrs: Record<string, string> = {}, text = '') => {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  if (text) e.textContent = text;
  return e;
};

/** m's variables, in the order its steps use them, each at its default (the macro's, else its first input's). */
export function launchVals(m: Macro, macros: Macro[]): { uses: Map<string, Field[]>; vals: Env } {
  const uses = varsOf(m, macros), vals: Env = {};
  for (const [n, fs] of uses) vals[n] = m.vars?.[n] ?? fs[0]?.dflt ?? '';
  return { uses, vals };
}

let dlg: HTMLDialogElement | null = null;

/**
 * The values to run m with: {} when it has no variables, else what its launch dialog has when Run is pressed (null:
 * cancelled). input makes each variable's input (as its first input has it).
 */
export function askVars(m: Macro, macros: Macro[], input: FieldInput): Promise<Env | null> {
  const { uses, vals } = launchVals(m, macros);
  if (!uses.size) return Promise.resolve({});
  if (!dlg) {
    dlg = el('dialog', { id: 'emaclaunch' }) as HTMLDialogElement;
    dlg.addEventListener('keydown', e => { if (!e.ctrlKey && !e.metaKey) e.stopPropagation(); });   // Esc closes it, not the selection
    document.body.append(dlg);
  }
  const d = dlg;
  d.textContent = '';
  const head = el('div', { class: 'ehead' }), close = el('button', { class: 'small', title: 'Esc: don\'t run it' }, '✕');
  head.append(el('b', {}, `Run ${m.name}`), close);
  const info = el('div', { class: 'mono' }), rows = el('div', { class: 'emlvars' });
  const run = el('button', { title: 'run it with these values (its saved defaults stay as they are)' }, 'Run') as HTMLButtonElement;
  const cancel = el('button', { class: 'small' }, 'Cancel');
  const say = (msg: string, warn = false) => { info.textContent = msg; info.classList.toggle('warn', warn); };
  /** Each row marked when its value doesn't fit every input it goes to; Run only when they all do. */
  const check = () => {
    let bad = 0;
    for (const [n, fs] of uses) {
      const no = fs.filter(f => fieldValue(f, vals[n]) === undefined), row = rows.querySelector(`[data-v="${n}"]`)!;
      row.classList.toggle('bad', !!no.length);
      row.setAttribute('title', no.length ? `${n} is ${JSON.stringify(vals[n])}: ${no.map(f => `${f.label || f.key} ${takes(f)}`).join('; ')}` : `<${n}>: ${fs.length} input${fs.length === 1 ? '' : 's'}`);
      bad += +!!no.length;
    }
    run.disabled = !!bad;
    if (bad) say(`${bad} value${bad === 1 ? '' : 's'} won't do (hover the marked row for why)`, true);
    else if (info.classList.contains('warn')) say('');
  };
  for (const [n, fs] of uses) {
    const row = el('div', { class: 'emvar', 'data-v': n });
    row.append(el('b', {}, `<${n}>`), input({ ...(fs[0] ?? anyField(n)), label: '' }, () => vals[n], v => { vals[n] = v; check(); }, say));
    rows.append(row);
  }
  const btns = el('div', { class: 'row' });
  btns.append(run, cancel);
  d.append(head, el('div', { class: 'help' }, 'its variables, at their defaults: change any for this run'), rows, btns, info);
  check();
  return new Promise(resolve => {
    let out: Env | null = null;
    run.onclick = () => { if (run.disabled) return; out = { ...vals }; d.close(); };
    close.onclick = cancel.onclick = () => d.close();
    d.onkeydown = e => {   // Enter runs it, with the value being typed in
      if (e.key !== 'Enter' || (e.target as HTMLElement).tagName === 'BUTTON' || (e.target as HTMLElement).tagName === 'SELECT') return;
      e.preventDefault();
      (e.target as HTMLElement).blur?.();
      run.click();
    };
    d.onclick = e => {   // a click on the backdrop (not the dialog's own padding)
      const r = d.getBoundingClientRect();
      if (e.target === d && (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom)) d.close();
    };
    d.onclose = () => { d.onclose = null; resolve(out); };
    d.showModal();
    (rows.querySelector('input, select') as HTMLElement | null)?.focus();
  });
}

/**
 * A plain input for value f takes (the object page's: no step list to fill choices in from); options: the choices for
 * a field the page fills in (a volume, a point source), null for a text input.
 */
export function plainInput(options: (f: Field) => [string, string][] | null): FieldInput {
  return (f, get, put, say) => {
    const cur = get(), what = f.label || f.key;
    if (f.k === 'bool') {
      const c = el('input', { type: 'checkbox' }) as HTMLInputElement;
      c.checked = !!cur;
      c.onchange = () => put(c.checked);
      return c;
    }
    if (f.k === 'num') {
      const i = el('input', { type: 'number', style: 'width:5em' }) as HTMLInputElement;
      if (f.min !== undefined) i.min = String(f.min);
      if (f.max !== undefined) i.max = String(f.max);
      i.step = String(f.step ?? 1);
      const show = (v: unknown) => (f.blank !== undefined && v === 0 ? '' : String(v));   // left empty: 0
      if (f.blank !== undefined) i.placeholder = f.blank;
      i.value = show(cur);
      i.onchange = () => { const v = i.value === '' ? (f.blank !== undefined ? 0 : NaN) : +i.value; if (fieldValue(f, v) === undefined) { i.value = show(get()); say(`${what} ${takes(f)}`, true); return; } put(v); };
      const unit = f.unit;
      if (!unit) return i;
      const l = el('label', {});
      l.append(i, unit === '°' ? unit : ' ' + unit);
      return l;
    }
    const opts = f.k === 'choice' ? f.options : options(f);
    if (!opts) {
      const i = el('input', { type: 'text', style: 'width:10em' }) as HTMLInputElement;
      i.value = String(cur);
      i.onchange = () => put(i.value.trim());
      return i;
    }
    const sl = el('select') as HTMLSelectElement, all = [...opts];
    if (!all.some(([v]) => v === cur)) all.push([String(cur), `${cur} (not here)`]);
    for (const [v, t] of all) sl.append(el('option', { value: v }, t));
    sl.value = String(cur);
    sl.onchange = () => put(sl.value);
    return sl;
  };
}
